import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { type SubmitEvent, useState } from 'react'

import type { CodeRequestedResponse } from '@/bindings/CodeRequestedResponse'
import type { LoginResponse } from '@/bindings/LoginResponse'
import type { RegisterRequest } from '@/bindings/RegisterRequest'
import type { RestaurantUserInfo } from '@/bindings/RestaurantUserInfo'
import type { VerifyRequest } from '@/bindings/VerifyRequest'
import { Button } from '@/components/button'
import { Field, Input, PasswordInput } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card } from '@/components/layout'
import { AuthPage } from '@/features/auth/auth-page'
import { codeRejected, useCodeStatus } from '@/features/auth/code-status'
import { CodeStatusPanel } from '@/features/auth/code-status-panel'
import { emptyIdentifierDraft, toLoginIdentifier } from '@/features/auth/identifier-draft'
import { IdentifierInput } from '@/features/auth/identifier-input'
import { PasswordChecklist } from '@/features/auth/password-checklist'
import { useSmsCoverage } from '@/features/auth/sms-coverage'
import { useAnonymousOnly } from '@/features/auth/use-anonymous-only'
import { ApiError, request } from '@/lib/api'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { PASSWORD_MIN_RESTAURANT, passwordProblem, useCommonPasswords } from '@/lib/password'
import { REGISTER_PATH, afterLoginPath } from '@/lib/return-path'
import { completeLogin } from '@/lib/session'

/**
 * Identifier-first registration (C6): submit only the identifier, then one
 * verify step sets code + password (+ optional names) and auto-logs-in.
 * The step lives in `?step=verify` so refresh/back keep it, and someone who
 * already holds a code (other device, manual phone OTP hours later) can jump
 * straight there and re-enter the identifier. Only the server's answer to
 * the last request is kept, per tab, for the verify step's status line (I1).
 */
export function RegisterPage() {
  const t = useT()
  const search = useSearch({ from: REGISTER_PATH })
  const navigate = useNavigate()
  const liveSessionLeave = useAnonymousOnly('restaurant', search)
  const language = useLanguage()
  const [identifier, setIdentifier] = useState(() => emptyIdentifierDraft())
  const { status, record: recordStatus, clear: clearStatus } = useCodeStatus('register', JSON.stringify(toLoginIdentifier(identifier)))
  const smsCovered = useSmsCoverage()
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [resent, setResent] = useState(false)
  // Client mirror of the backend rules: a green checklist submits clean.
  const commonPasswords = useCommonPasswords()
  const [showProblem, setShowProblem] = useState(false)
  const problem = passwordProblem(password, PASSWORD_MIN_RESTAURANT, commonPasswords)
  const step = search.step ?? 'request'

  // Also the resend: re-registering supersedes the email code / keeps the
  // open phone request (C6) — the answer is uniform, identifier taken or not.
  const register = useMutation({
    mutationFn: () => {
      const body: RegisterRequest = { identifier: toLoginIdentifier(identifier), language }
      return request<CodeRequestedResponse>('/auth/register', { method: 'POST', body })
    },
    onSuccess: recordStatus,
  })

  const verify = useMutation({
    mutationFn: () => {
      const body: VerifyRequest = {
        identifier: toLoginIdentifier(identifier),
        code: code.trim(),
        password,
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        preferred_language: language,
      }
      return request<LoginResponse<RestaurantUserInfo>>('/auth/verify', { method: 'POST', body })
    },
    onSuccess: (response) => {
      clearStatus()
      completeLogin('restaurant', response)
      return navigate({ to: afterLoginPath(search, 'restaurant'), replace: true })
    },
  })

  if (liveSessionLeave) return liveSessionLeave

  const toVerify = () => navigate({ to: REGISTER_PATH, search: { ...search, step: 'verify' } })

  function submitRegister(event: SubmitEvent) {
    event.preventDefault()
    register.mutate(undefined, { onSuccess: () => void toVerify() })
  }

  function skipToVerify() {
    register.reset()
    void toVerify()
  }

  function resend() {
    register.mutate(undefined, { onSuccess: () => setResent(true) })
  }

  function switchToEmail() {
    register.reset()
    setIdentifier({ ...identifier, channel: 'email' })
    void navigate({ to: REGISTER_PATH, search: { ...search, step: undefined } })
  }

  function submitVerify(event: SubmitEvent) {
    event.preventDefault()
    if (problem) {
      setShowProblem(true)
      return
    }
    verify.mutate()
  }

  // Password rules are the backend's (C2); PASSWORD_* lands on its field, the rest form-level.
  const passwordError =
    verify.error instanceof ApiError && verify.error.code.startsWith('PASSWORD_')
      ? verify.error
      : showProblem && problem
        ? t(problem.key, problem.params)
        : undefined

  return (
    <AuthPage>
      {step === 'request' ? (
        <Card className="flex flex-col gap-4 p-6">
          <h1 className="text-xl font-semibold">{t('auth.register')}</h1>
          <p className="text-muted text-sm">{t('auth.registerIntro')}</p>
          <form onSubmit={submitRegister} className="flex flex-col gap-4">
            <IdentifierInput value={identifier} onChange={setIdentifier} smsHint />
            <FormError error={register.error} />
            <Button type="submit" loading={register.isPending} block>
              {t('auth.sendCode')}
            </Button>
          </form>
          <div className="flex flex-col items-center gap-2 text-sm">
            <button type="button" onClick={skipToVerify} className="text-accent-ink font-medium hover:underline">
              {t('auth.haveCode')}
            </button>
            <Link to="/app/login" search={search} className="text-accent-ink font-medium hover:underline">
              {t('auth.login')}
            </Link>
          </div>
        </Card>
      ) : (
        <Card className="flex flex-col gap-4 p-6">
          <h1 className="text-xl font-semibold">{t('auth.verifyTitle')}</h1>
          <form onSubmit={submitVerify} className="flex flex-col gap-4">
            <IdentifierInput value={identifier} onChange={setIdentifier} />
            <Field label={t('auth.code')}>
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
            <Field
              label={t('auth.password')}
              hint={<PasswordChecklist password={password} min={PASSWORD_MIN_RESTAURANT} />}
              error={passwordError}
            >
              <PasswordInput
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <Field label={`${t('auth.firstName')} (${t('common.optional')})`}>
              <Input autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </Field>
            <Field label={`${t('auth.lastName')} (${t('common.optional')})`}>
              <Input autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </Field>
            {resent && (
              <p role="status" className="text-muted text-sm">
                {t('auth.codeRequested')}
              </p>
            )}
            <FormError error={passwordError ? undefined : verify.error} />
            <FormError error={register.error} />
            <Button type="submit" loading={verify.isPending} block>
              {t('auth.createAccount')}
            </Button>
            <CodeStatusPanel
              status={status}
              channel={identifier.channel}
              flow="verify"
              smsCovered={smsCovered(identifier.nation)}
              codeRejected={codeRejected(verify.error)}
              onResend={resend}
              resendPending={register.isPending}
              onUseEmail={switchToEmail}
            />
          </form>
        </Card>
      )}
    </AuthPage>
  )
}
